import React from "react";
import { Dialog, DialogContent, Button, useTheme } from "@mui/material";
import { CrossIcon } from "../Icons";
import { colors } from "../../theme/colors";
import { ActionsContainer, CrossButton, ModalTitle } from "./Modal.styled";

type ButtonStyles = React.CSSProperties & {
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
};

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  primaryButtonStyles?: ButtonStyles;
  secondaryButtonStyles?: ButtonStyles;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false | string | number;
  fullWidth?: boolean;
  contentPadding?: string;
  containerPadding?: string;
  width?: string | number;
  height?: string | number;
  minWidth?: string | number;
  minHeight?: string | number;
  maxHeight?: string | number;
};

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  showCloseButton,
  closeOnBackdropClick = true,
  primaryButtonText,
  secondaryButtonText,
  primaryButtonStyles,
  secondaryButtonStyles,
  onPrimaryClick,
  onSecondaryClick,
  maxWidth = "sm",
  fullWidth = true,
  contentPadding = "24px",
  containerPadding = "60px",
  width,
  height,
  minWidth,
  minHeight,
  maxHeight,
}) => {
  const theme = useTheme();

  const handleClose = (
    event: object,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    // If closeOnBackdropClick is false and reason is backdropClick, don't close
    if (!closeOnBackdropClick && reason === "backdropClick") {
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={
        typeof maxWidth === "string" &&
        ["xs", "sm", "md", "lg", "xl"].includes(maxWidth)
          ? (maxWidth as "xs" | "sm" | "md" | "lg" | "xl")
          : false
      }
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: colors.shadow.modal,
          justifyContent: "center",
          alignItems: "center",
          padding: containerPadding,
          maxHeight: "90vh",
          overflow: "auto",
          transition: "border-radius 0.2s ease, padding 0.2s ease, margin 0.2s ease, max-height 0.2s ease, width 0.2s ease",
          ...(width && { width }),
          ...(height && { height }),
          ...(minWidth && { minWidth }),
          ...(minHeight && { minHeight }),
          ...(maxHeight && { maxHeight }),
          ...(typeof maxWidth === "string" &&
            !["xs", "sm", "md", "lg", "xl"].includes(maxWidth) && {
              maxWidth,
            }),
          ...(typeof maxWidth === "number" && { maxWidth }),
          [theme.breakpoints.down("sm")]: {
            borderRadius: "16px",
            padding: "16px",
            margin: "16px",
            maxHeight: "85vh",
            width: "calc(100% - 32px)",
          },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(20, 25, 33, 0.70)",
        },
      }}
    >
      {showCloseButton && (
        <CrossButton onClick={onClose} size="small">
          <CrossIcon width="32px" height="32px" fill={colors.text.primary} />
        </CrossButton>
      )}
      {title && <ModalTitle>{title}</ModalTitle>}

      <DialogContent
        sx={{
          px: 0,
          py: 0,
          mt: contentPadding === "0" ? 0 : 2,
          width: "100%",
          overflow: "auto",
          transition: "margin-top 0.2s ease, max-height 0.2s ease",
          [theme.breakpoints.down("sm")]: {
            mt: contentPadding === "0" ? 0 : 1,
            maxHeight: "calc(85vh - 120px)",
          },
        }}
      >
        {children}
      </DialogContent>

      {(primaryButtonText || secondaryButtonText) && (
        <ActionsContainer>
          {secondaryButtonText && (
            <Button
              variant="outlined"
              onClick={onSecondaryClick || onClose}
              sx={{
                borderColor:
                  secondaryButtonStyles?.borderColor || colors.primary.main,
                color: secondaryButtonStyles?.color || colors.primary.main,
                "&:hover": {
                  borderColor:
                    secondaryButtonStyles?.hoverBorderColor ||
                    colors.primary.dark,
                  backgroundColor:
                    secondaryButtonStyles?.hoverBackgroundColor ||
                    "rgba(58, 138, 185, 0.04)",
                },
                ...secondaryButtonStyles,
              }}
            >
              {secondaryButtonText}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              variant="contained"
              onClick={onPrimaryClick}
              sx={{
                backgroundColor:
                  primaryButtonStyles?.backgroundColor || colors.primary.main,
                color: primaryButtonStyles?.color || "#ffffff",
                "&:hover": {
                  backgroundColor:
                    primaryButtonStyles?.hoverBackgroundColor ||
                    colors.primary.dark,
                },
                ...primaryButtonStyles,
              }}
            >
              {primaryButtonText}
            </Button>
          )}
        </ActionsContainer>
      )}
    </Dialog>
  );
};

export default Modal;
